/**
 * Multi-Chain Deployment Configuration
 * Supports two modes:
 * - "sequential": Frontend handles chain switching and multiple deployments
 * - "relayer": Single transaction, backend deploys to other chains
 */

// Deployment mode from env
export const MULTICHAIN_MODE = process.env.NEXT_PUBLIC_MULTICHAIN_MODE || 'sequential'
export const isSequentialMode = MULTICHAIN_MODE === 'sequential'
export const isRelayerMode = MULTICHAIN_MODE === 'relayer'

// Relayer API endpoint
export const RELAYER_API_URL = process.env.NEXT_PUBLIC_RELAYER_API_URL || '/api/relay-deploy'

// Chain configurations for multi-chain deployment
export interface ChainConfig {
    chainId: number
    name: string
    rpcUrl: string
    nativeCurrency: {
        name: string
        symbol: string
        decimals: number
    }
    blockExplorer: string
    factoryAddress: string  // MultiCurrencyPresaleFactory for primary chain
    collectorFactoryAddress: string  // ContributionCollectorFactory for secondary chains
}

// Import chain IDs from contracts-config
import {
    TESTNET_CHAIN_IDS,
    MAINNET_CHAIN_IDS,
    isTestnet,
    getContractAddress
} from './contracts-config'

// Testnet chain configs
export const TESTNET_CHAINS: Record<string, ChainConfig> = {
    BSC: {
        chainId: TESTNET_CHAIN_IDS.BSC_TESTNET,
        name: 'BSC Testnet',
        rpcUrl: process.env.NEXT_PUBLIC_BSC_TESTNET_RPC || 'https://data-seed-prebsc-1-s1.binance.org:8545',
        nativeCurrency: { name: 'tBNB', symbol: 'tBNB', decimals: 18 },
        blockExplorer: process.env.NEXT_PUBLIC_BSC_TESTNET_EXPLORER || 'https://testnet.bscscan.com',
        factoryAddress: process.env.NEXT_PUBLIC_PRESALE_FACTORY_BSC_TESTNET || '',
        collectorFactoryAddress: process.env.NEXT_PUBLIC_COLLECTOR_FACTORY_BSC_TESTNET || '',
    },
    ETH: {
        chainId: TESTNET_CHAIN_IDS.SEPOLIA,
        name: 'Sepolia',
        rpcUrl: process.env.NEXT_PUBLIC_SEPOLIA_RPC || 'https://ethereum-sepolia-rpc.publicnode.com',
        nativeCurrency: { name: 'SepoliaETH', symbol: 'ETH', decimals: 18 },
        blockExplorer: process.env.NEXT_PUBLIC_SEPOLIA_EXPLORER || 'https://sepolia.etherscan.io',
        factoryAddress: process.env.NEXT_PUBLIC_PRESALE_FACTORY_SEPOLIA || '',
        collectorFactoryAddress: process.env.NEXT_PUBLIC_COLLECTOR_FACTORY_SEPOLIA || '',
    },
    POLYGON: {
        chainId: TESTNET_CHAIN_IDS.POLYGON_AMOY,
        name: 'Polygon Amoy',
        rpcUrl: process.env.NEXT_PUBLIC_POLYGON_AMOY_RPC || 'https://rpc-amoy.polygon.technology',
        nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
        blockExplorer: process.env.NEXT_PUBLIC_POLYGON_AMOY_EXPLORER || 'https://www.oklink.com/amoy',
        factoryAddress: process.env.NEXT_PUBLIC_PRESALE_FACTORY_POLYGON_TESTNET || '',
        collectorFactoryAddress: process.env.NEXT_PUBLIC_COLLECTOR_FACTORY_POLYGON_TESTNET || '',
    },
    BASE: {
        chainId: TESTNET_CHAIN_IDS.BASE_SEPOLIA,
        name: 'Base Sepolia',
        rpcUrl: process.env.NEXT_PUBLIC_BASE_SEPOLIA_RPC || 'https://sepolia.base.org',
        nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
        blockExplorer: process.env.NEXT_PUBLIC_BASE_SEPOLIA_EXPLORER || 'https://sepolia.basescan.org',
        factoryAddress: process.env.NEXT_PUBLIC_PRESALE_FACTORY_BASE_TESTNET || '',
        collectorFactoryAddress: process.env.NEXT_PUBLIC_COLLECTOR_FACTORY_BASE_TESTNET || '',
    },
}

// Mainnet chain configs
export const MAINNET_CHAINS: Record<string, ChainConfig> = {
    BSC: {
        chainId: MAINNET_CHAIN_IDS.BSC,
        name: 'BNB Smart Chain',
        rpcUrl: process.env.NEXT_PUBLIC_BSC_RPC || 'https://bsc-dataseed1.binance.org',
        nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
        blockExplorer: process.env.NEXT_PUBLIC_BSC_EXPLORER || 'https://bscscan.com',
        factoryAddress: process.env.NEXT_PUBLIC_PRESALE_FACTORY_BSC || '',
        collectorFactoryAddress: process.env.NEXT_PUBLIC_COLLECTOR_FACTORY_BSC || '',
    },
    ETH: {
        chainId: MAINNET_CHAIN_IDS.ETH,
        name: 'Ethereum',
        rpcUrl: process.env.NEXT_PUBLIC_ETH_RPC || 'https://eth.llamarpc.com',
        nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
        blockExplorer: process.env.NEXT_PUBLIC_ETH_EXPLORER || 'https://etherscan.io',
        factoryAddress: process.env.NEXT_PUBLIC_PRESALE_FACTORY_ETH || '',
        collectorFactoryAddress: process.env.NEXT_PUBLIC_COLLECTOR_FACTORY_ETH || '',
    },
    POLYGON: {
        chainId: MAINNET_CHAIN_IDS.POLYGON,
        name: 'Polygon',
        rpcUrl: process.env.NEXT_PUBLIC_POLYGON_RPC || 'https://polygon-rpc.com',
        nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
        blockExplorer: process.env.NEXT_PUBLIC_POLYGON_EXPLORER || 'https://polygonscan.com',
        factoryAddress: process.env.NEXT_PUBLIC_PRESALE_FACTORY_POLYGON || '',
        collectorFactoryAddress: process.env.NEXT_PUBLIC_COLLECTOR_FACTORY_POLYGON || '',
    },
    BASE: {
        chainId: MAINNET_CHAIN_IDS.BASE,
        name: 'Base',
        rpcUrl: process.env.NEXT_PUBLIC_BASE_RPC || 'https://mainnet.base.org',
        nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
        blockExplorer: process.env.NEXT_PUBLIC_BASE_EXPLORER || 'https://basescan.org',
        factoryAddress: process.env.NEXT_PUBLIC_PRESALE_FACTORY_BASE || '',
        collectorFactoryAddress: process.env.NEXT_PUBLIC_COLLECTOR_FACTORY_BASE || '',
    },
}

// Get active chains based on network mode
export const CHAIN_CONFIGS = isTestnet ? TESTNET_CHAINS : MAINNET_CHAINS

/**
 * Get chain config by network key
 */
export function getChainConfig(networkKey: string): ChainConfig | null {
    return CHAIN_CONFIGS[networkKey] || null
}

/**
 * Get chain config by chain ID
 */
export function getChainConfigByChainId(chainId: number): ChainConfig | null {
    const entries = Object.entries(CHAIN_CONFIGS)
    for (const [_, config] of entries) {
        if (config.chainId === chainId) {
            return config
        }
    }
    return null
}

/**
 * Get all supported network keys
 */
export function getSupportedNetworks(): string[] {
    return Object.keys(CHAIN_CONFIGS)
}

/**
 * Check if a network is supported (has factory deployed)
 */
export function isNetworkSupported(networkKey: string): boolean {
    const config = CHAIN_CONFIGS[networkKey]
    return !!(config && config.factoryAddress)
}
